import { logger } from '@/lib/logger';

import { type Scheme } from './defaults';
import { type ThemeNote } from './tokens';

/**
 * The only module in the app that writes theme CSS or document chrome. Steps 8–9 of
 * `docs/Branding-And-Theming.md` §9.
 *
 * Everything here is a CSSOM property write or an attribute update on an element that already exists
 * in `index.html`. Both matter for the CSP (ADR-026):
 *
 * - `setProperty` is a CSSOM write and is **not** governed by `style-src 'self'`. `style.cssText` and
 *   `setAttribute('style', …)` *are* governed and would be blocked, which is why ESLint bans them —
 *   they look equivalent and would fail only at runtime, in production, on the branding path.
 * - `theme-color` and the favicon are **mutated in place** rather than rendered by React. React 19
 *   appends hoisted `<meta>` elements and browsers honour the *first* `theme-color`, so a rendered
 *   one would be silently ignored (measured; see ADR-030).
 */

export function applyThemeTokens(
  vars: Readonly<Record<string, string>>,
  root: HTMLElement = document.documentElement,
): void {
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
}

/**
 * Reflects the effective scheme in the places that render before or outside our CSS.
 *
 * `color-scheme` is what stops a white flash in browser-painted UI — scrollbars, form controls, and
 * the address bar on some browsers — before our stylesheet applies.
 */
export function applyScheme(scheme: Scheme, root: HTMLElement = document.documentElement): void {
  root.classList.toggle('dark', scheme === 'dark');
  setMeta('color-scheme', scheme === 'dark' ? 'dark light' : 'light dark');
}

function setMeta(name: string, content: string): void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (existing) {
    existing.setAttribute('content', content);
    return;
  }
  const meta = document.createElement('meta');
  meta.setAttribute('name', name);
  meta.setAttribute('content', content);
  document.head.appendChild(meta);
}

export interface DocumentChrome {
  /** Already validated `#RRGGBB`. */
  themeColor: string;
  /** Already passed `isSafeAssetUrl`, or `null` to keep the bundled default. */
  faviconUrl?: string | null | undefined;
}

export function applyDocumentChrome({ themeColor, faviconUrl }: DocumentChrome): void {
  setMeta('theme-color', themeColor);

  if (faviconUrl == null || faviconUrl === '') return;
  const link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  // Mutating the existing element rather than appending: two `rel="icon"` links resolve differently
  // across browsers, and a favicon is a trust signal we would rather have plain than ambiguous.
  if (link) {
    link.setAttribute('href', faviconUrl);
    link.removeAttribute('type');
  }
}

/**
 * DEV-only. These logs are how we tell a merchant their palette needs work.
 *
 * The payload key is `cssVariable`, not `token`: `token` is on the logger's redaction list because it
 * means the session credential everywhere else in the app, so a CSS token name under that key would
 * be logged as `[REDACTED]` and the log would be useless. Redaction stays broad; the caller renames.
 */
export function logThemeNotes(notes: readonly ThemeNote[]): void {
  for (const note of notes) {
    logger.debug('[theme] token substituted', {
      cssVariable: note.token,
      reason: note.reason,
      rejected: note.rejected,
      replacement: note.replacement,
      ...(note.ratio === undefined ? {} : { ratio: note.ratio }),
    });
  }
}

export interface ApplyBrandingInput {
  vars: Readonly<Record<string, string>>;
  scheme: Scheme;
  notes?: readonly ThemeNote[] | undefined;
  faviconUrl?: string | null | undefined;
}

/**
 * Applies everything in one call so a caller cannot apply half a theme.
 *
 * Called exactly once per session — or again only on a `matchMedia` change under
 * `colorScheme: 'system'`. Re-applying on a poll response produces a visible flash on some devices,
 * and a payment page that flickers every four seconds reads as broken or malicious.
 */
export function applyBranding({ vars, scheme, notes, faviconUrl }: ApplyBrandingInput): void {
  applyThemeTokens(vars);
  applyScheme(scheme);

  const themeColor = vars['--vp-primary'];
  if (themeColor !== undefined) {
    applyDocumentChrome({ themeColor, faviconUrl });
  }

  if (notes && notes.length > 0) logThemeNotes(notes);
}
